import React, { useState, useEffect, useCallback } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import {
  ChartLine, Trophy, ArrowRight, CaretLeft, Rocket, Question, ShareNetwork,
} from '@phosphor-icons/react';
import { WinnerCard, shareSeoPage } from './HallOfWinners';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const handleLogin = () => {
  const redirectUrl = window.location.origin + '/dashboard';
  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
};

// ─── Curated SEO content per niche (unique copy = indexable value) ───
export const NICHE_CONTENT = {
  'emagrecimento': {
    label: 'Emagrecimento',
    intro: 'O nicho de emagrecimento é dos mais competitivos (e lucrativos) do marketing digital em português. Os funis vencedores combinam VSLs com storytelling de transformação, provas visuais antes/depois e ofertas com stack de bónus nutricionais. Abaixo estão os funis deste nicho com melhor Winner Score™ — avaliados pela nossa IA em 25+ indicadores.',
    faqs: [
      { q: 'O que converte melhor em funis de emagrecimento?', a: 'VSLs de 12-20 minutos com mecanismo único ("o método X que destrava o metabolismo"), prova social visual (antes/depois reais) e garantia forte. Quizzes de diagnóstico como pre-frame duplicam a conversão da página de vendas.' },
      { q: 'Qual o ticket médio típico neste nicho?', a: 'Produtos front-end entre €27-67 (protocolos, desafios de 21/30 dias) com order bumps de €17-27 e upsells de acompanhamento (€97-197). O lifetime value cresce com comunidades de acompanhamento mensal.' },
      { q: 'Como espiar os anúncios dos concorrentes de emagrecimento?', a: 'Usa o Discovery do FunnelHack AI: escreve "emagrecimento" e recebes os players ativos com botões diretos para os anúncios deles no Facebook Ads Library, Google, YouTube e TikTok.' },
    ],
  },
  'renda-extra': {
    label: 'Renda Extra',
    intro: 'Funis de renda extra e dinheiro online vivem de promessas específicas com prazo ("€3.000/mês em 90 dias") e prova social de alunos. Os vencedores usam webinários e desafios pagos de baixo ticket como porta de entrada. Estes são os funis do nicho com melhor Winner Score™.',
    faqs: [
      { q: 'Que estrutura de funil domina o nicho de renda extra?', a: 'Desafio pago de €7-27 → oferta principal €297-997 no fim do desafio. A taxa de conversão do desafio para a oferta principal ronda 10-20% quando há acompanhamento diário e comunidade ativa.' },
      { q: 'Como criar credibilidade num nicho cheio de promessas falsas?', a: 'Scarcity real, resultados verificáveis de alunos (prints com data), e garantia "stronger than money-back". Os funis com Winner Score™ mais alto evitam promessas absolutas e ancoram em casos concretos.' },
      { q: 'Vale a pena modelar funis internacionais?', a: 'Sim — muitos vencedores em PT são adaptações de funis americanos com 12-18 meses de maturidade. Usa o Funnel Spy para dissecar a estrutura e o AI Composer para adaptar o copy ao mercado lusófono.' },
    ],
  },
  'marketing-digital': {
    label: 'Marketing Digital',
    intro: 'Vender marketing para marketeers exige autoridade demonstrada: os funis vencedores deste nicho mostram resultados próprios em tempo real, usam mecanismos únicos com nome próprio e lançamentos sazonais. Aqui estão os funis com melhor Winner Score™ do nicho.',
    faqs: [
      { q: 'O que diferencia um funil vencedor de marketing digital?', a: 'Mecanismo único nomeado (ex: "Método Lançamento Invisível"), demonstração ao vivo do resultado (dashboards, métricas reais) e escada de valor clara: conteúdo grátis → produto €47-97 → mentoria €997+.' },
      { q: 'Lançamento ou funil perpétuo — o que rende mais?', a: 'Lançamentos geram picos de 6 dígitos mas exigem audiência; funis perpétuos com VSL + tráfego pago escalam de forma previsível. Os melhores players do Hall combinam: perpétuo para cashflow, lançamento 2-3x/ano para picos.' },
      { q: 'Como acompanhar as mudanças dos concorrentes?', a: 'O Monitoring do FunnelHack AI verifica os funis dos teus concorrentes a cada 6 horas e envia alertas quando mudam headlines, preços ou estrutura — com timeline visual das alterações.' },
    ],
  },
  'desenvolvimento-pessoal': {
    label: 'Desenvolvimento Pessoal',
    intro: 'Produtividade, hábitos, mindset: o nicho de desenvolvimento pessoal converte com transformação identitária ("torna-te a pessoa que...") em vez de features. Os funis vencedores usam storytelling pessoal profundo e comunidades de accountability. Estes são os melhores do nicho por Winner Score™.',
    faqs: [
      { q: 'Que gatilhos funcionam em desenvolvimento pessoal?', a: 'Identidade futura ("a tua versão 2.0"), custo da inação (quanto perdes por adiar) e pertença a uma tribo. Funis com quiz de autodiagnóstico no topo têm CPLs 30-50% mais baixos.' },
      { q: 'Qual o formato de produto que mais escala?', a: 'Desafios de 21-30 dias com comunidade + app/área de membros. O ticket front-end ronda €47-97 e a retenção vem de subscrições de continuidade (€19-39/mês).' },
      { q: 'Como encontrar os funis vencedores deste nicho?', a: 'No Discovery do FunnelHack AI escreve "produtividade" ou "desenvolvimento pessoal" — a IA devolve os players ativos e os anúncios que estão a escalar agora em cada plataforma.' },
    ],
  },
  'ingles-idiomas': {
    label: 'Inglês & Idiomas',
    intro: 'O nicho de idiomas (dominado pelo inglês) é um dos mais estáveis: dor perene, público massivo, tickets acessíveis. Os funis vencedores prometem fluência com prazo + método anti-gramática e usam demonstrações em vídeo do progresso de alunos. Vê os melhores por Winner Score™.',
    faqs: [
      { q: 'O que promete um funil vencedor de inglês?', a: '"Fluência em X meses sem decorar gramática" — mecanismo único (imersão, shadowing, storytelling) com aula-demonstração gratuita como lead magnet. A aula ao vivo converte 3-8% para ofertas de €297-997.' },
      { q: 'Qual a maior alavanca de conversão neste nicho?', a: 'Prova em vídeo: alunos reais a falar inglês após o método. Nada supera ver a transformação. Funis com 3+ vídeos de alunos no meio da página têm scores consistentemente superiores.' },
      { q: 'Como analisar um funil de idiomas concorrente?', a: 'Cola o URL no Funnel Spy do FunnelHack AI: em ~30 segundos recebes a estrutura completa, headlines, ofertas, order bumps e uma estimativa de conversão e ticket.' },
    ],
  },
  'financas-investimentos': {
    label: 'Finanças & Investimentos',
    intro: 'Finanças pessoais, investimentos e trading: nicho de ticket alto e escrutínio elevado. Os funis vencedores equilibram promessa e compliance, mostram track record auditável e usam webinários de conversão. Estes são os funis do nicho com melhor Winner Score™.',
    faqs: [
      { q: 'Como vender investimentos sem promessas ilegais?', a: 'Os vencedores vendem o processo e a competência ("aprende a analisar como um profissional"), nunca retornos garantidos. Disclaimers visíveis aumentam (não diminuem) a confiança e a conversão em públicos sofisticados.' },
      { q: 'Que formato converte melhor: ebook, curso ou mentoria?', a: 'Escada completa: lead magnet de análise/planilha grátis → curso gravado €197-497 → mentoria/sala ao vivo €997-4997. O webinário semanal ao vivo continua a ser o maior conversor de ticket alto.' },
      { q: 'Como monitorizar concorrentes de finanças?', a: 'Adiciona os funis deles ao Monitoring do FunnelHack AI — recebes alertas quando mudam a oferta ou o preço, e a timeline mostra a evolução da estratégia deles ao longo do tempo.' },
    ],
  },
  'beleza-estetica': {
    label: 'Beleza & Estética',
    intro: 'Skincare, maquilhagem, procedimentos: o nicho de beleza converte com resultados visuais imediatos e rotinas passo-a-passo. Os funis vencedores usam vídeo curto (o nicho mais forte em TikTok/Reels) e ofertas de protocolo. Vê os melhores por Winner Score™.',
    faqs: [
      { q: 'Qual a plataforma de tráfego dominante em beleza?', a: 'TikTok e Instagram Reels — vídeos de transformação de 15-30s com CTA para quiz de diagnóstico de pele/rotina. Usa os botões TikTok do Discovery para veres os top ads do nicho.' },
      { q: 'Que produto digital funciona em beleza?', a: 'Protocolos de rotina (€27-67), consultorias personalizadas (€97+) e clubes de subscrição com novidades mensais. O order bump clássico: guia de produtos low-cost que replicam resultados premium.' },
      { q: 'Como validar uma oferta antes de investir em tráfego?', a: 'Usa o Winner Score™: cola o URL da tua página e a IA avalia 25+ indicadores (headline, prova, oferta, risco) e diz-te exatamente o que corrigir antes de escalar.' },
    ],
  },
  'fitness-musculacao': {
    label: 'Fitness & Musculação',
    intro: 'Hipertrofia, treino em casa, consultoria fitness: os funis vencedores vendem o plano personalizado e a identidade ("atleta", "shape de verão"). Transformações corporais documentadas são a moeda do nicho. Estes são os funis com melhor Winner Score™.',
    faqs: [
      { q: 'O que converte melhor: app, PDF ou consultoria?', a: 'Front-end: desafios de 8-12 semanas (€47-97) com grupo. Back-end: consultoria individualizada (€150-500/mês). Apps próprias só compensam com 1.000+ alunos ativos — antes disso, comunidade + planilha ganham.' },
      { q: 'Como se diferenciar num nicho saturado?', a: 'Sub-nicho + mecanismo: "hipertrofia para ectomorfos", "treino em casa para mães". Os funis generalistas têm scores baixos; os vencedores dominam um ângulo específico com prova dedicada.' },
      { q: 'Onde ver os anúncios dos maiores players fitness?', a: 'No Discovery do FunnelHack AI: pesquisa o nicho e abre os anúncios ativos de cada player no Facebook Ads Library, YouTube e TikTok com um clique.' },
    ],
  },
  'relacionamentos': {
    label: 'Relacionamentos',
    intro: 'Conquista, reconquista, casamento: o nicho de relacionamentos tem das dores mais urgentes do mercado — e funis de resposta direta agressivos. Os vencedores usam storytelling emocional profundo e mecanismos psicológicos nomeados. Vê os melhores por Winner Score™.',
    faqs: [
      { q: 'Que ângulo domina o nicho de relacionamentos?', a: 'Urgência emocional + mecanismo específico: "o gatilho da reconexão", "as 3 fases da reconquista". VSLs longas (20-35 min) com storytelling em primeira pessoa superam consistentemente formatos curtos.' },
      { q: 'Qual o perfil de oferta típico?', a: 'Front-end €37-67 (método/protocolo), order bump de scripts prontos (€17), upsell de acompanhamento ou análise personalizada (€97-197). Alto volume de compras por impulso — o checkout precisa de ser friccionless.' },
      { q: 'Como estudar os funis deste nicho de forma ética?', a: 'O Funnel Spy analisa qualquer página pública e o Hall of Winners mostra padrões anonimizados — estuda a estrutura e os gatilhos sem copiar o conteúdo.' },
    ],
  },
  'maternidade': {
    label: 'Maternidade & Família',
    intro: 'Sono do bebé, introdução alimentar, educação positiva: o nicho de maternidade converte com empatia genuína e autoridade técnica (consultoras certificadas). Os funis vencedores usam conteúdo educativo gratuito robusto antes de vender. Estes são os melhores por Winner Score™.',
    faqs: [
      { q: 'O que faz uma mãe comprar um produto digital?', a: 'Confiança primeiro: os funis vencedores entregam muito valor grátis (guias, lives, grupos) antes da oferta. A promessa vencedora resolve UMA dor específica ("o teu bebé a dormir a noite toda em 14 dias").' },
      { q: 'Qual o ticket viável neste nicho?', a: 'Front-end €47-97 (cursos de sono, alimentação), consultorias individuais €150-400. A recorrência vem de comunidades de acompanhamento por fase (0-6m, 6-12m...) — retenção altíssima quando o conteúdo acompanha o crescimento.' },
      { q: 'Como encontrar as consultoras que mais escalam?', a: 'Pesquisa "maternidade" ou "sono do bebé" no Discovery do FunnelHack AI e vê os anúncios ativos de cada player — os que anunciam há 6+ meses têm funis validados que vale a pena dissecar.' },
    ],
  },
};

const NicheWinners = () => {
  const { slug } = useParams();
  const { t } = useTranslation();
  const content = NICHE_CONTENT[slug];
  const [items, setItems] = useState([]);
  const [niches, setNiches] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [res, nres] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/public/hall-of-winners?limit=24&min_score=70&niche=${slug}`),
        axios.get(`${BACKEND_URL}/api/public/winners/niches`),
      ]);
      setItems(res.data.items || []);
      setNiches(nres.data.niches || []);
    } catch (e) { /* ignore */ }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => {
    if (!content) return;
    document.title = `Melhores Funis de ${content.label} · Hall of Winners™ | FunnelHack AI`;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement('meta');
      meta.name = 'description';
      document.head.appendChild(meta);
    }
    meta.content = `Os funis de ${content.label.toLowerCase()} com melhor Winner Score™ — analisados por IA em 25+ indicadores. Estruturas, gatilhos e padrões dos vencedores do nicho.`;
    // JSON-LD FAQPage for rich results
    const ld = document.createElement('script');
    ld.type = 'application/ld+json';
    ld.id = 'niche-faq-ld';
    ld.text = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: content.faqs.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    });
    document.getElementById('niche-faq-ld')?.remove();
    document.head.appendChild(ld);
    load();
    return () => document.getElementById('niche-faq-ld')?.remove();
  }, [content, load]);

  if (!content) return <Navigate to="/hall-of-winners" replace />;

  return (
    <div className="min-h-screen bg-[#050505] text-white" data-testid="niche-winners-page">
      <header className="fixed top-0 w-full z-50 bg-black/60 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-gradient-to-br from-[#2563EB] to-[#3B82F6] rounded-lg flex items-center justify-center">
              <ChartLine size={24} weight="bold" className="text-white" />
            </div>
            <span className="text-xl font-[Outfit] font-bold tracking-tight">FunnelHack AI</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/hall-of-winners" data-testid="niche-back-btn" className="px-4 py-2 bg-white/5 hover:bg-white/10 rounded-lg text-sm flex items-center gap-1.5">
              <CaretLeft size={14} weight="bold" /> Hall of Winners
            </Link>
            <button onClick={handleLogin} data-testid="niche-cta-header" className="px-5 py-2 bg-gradient-to-r from-[#2563EB] to-[#3B82F6] rounded-lg text-sm font-semibold">
              {t('hall.trial')}
            </button>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="relative pt-36 pb-10 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#F59E0B]/15 border border-[#F59E0B]/30 rounded-full mb-6">
            <Trophy size={14} weight="fill" className="text-[#F59E0B]" />
            <span className="text-[11px] tracking-[0.25em] uppercase text-[#F59E0B] font-semibold">Hall of Winners™ · {content.label}</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-[Outfit] font-bold tracking-tight mb-6">
            {t('nw.title_pre')}{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#F59E0B] to-[#FBBF24]">{content.label}</span>
          </h1>
          <p className="text-base md:text-lg text-white/65 leading-relaxed max-w-3xl" data-testid="niche-intro">
            {content.intro}
          </p>
          <button
            onClick={() => shareSeoPage(`/api/og/winners/${slug}`, t('hall.share_copied'))}
            data-testid="niche-share-btn"
            className="mt-6 inline-flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F59E0B]/40 rounded-lg text-sm text-white/70 hover:text-white transition"
          >
            <ShareNetwork size={14} weight="bold" className="text-[#F59E0B]" />
            {t('hall.share_btn')}
          </button>
        </div>
      </section>

      {/* Winner cards */}
      <section className="px-6 pb-12">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="text-center py-14 text-white/40">{t('nw.loading')}</div>
          ) : items.length === 0 ? (
            <div className="p-8 bg-[#0A0A0A] border border-dashed border-white/15 rounded-2xl text-center" data-testid="niche-empty">
              <Trophy size={32} weight="bold" className="text-white/20 mx-auto mb-3" />
              <p className="text-white/60 max-w-xl mx-auto leading-relaxed">
                {t('nw.empty_p1', { niche: content.label.toLowerCase() })}
              </p>
              <button onClick={handleLogin} data-testid="niche-empty-cta"
                className="mt-5 inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] rounded-xl text-sm font-semibold text-black">
                <Trophy size={14} weight="fill" /> {t('nw.empty_cta')}
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="niche-grid">
              {items.map((it) => <WinnerCard key={it.score_id} item={it} />)}
            </div>
          )}
        </div>
      </section>

      {/* FAQ (SEO content) */}
      <section className="px-6 pb-16">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl md:text-3xl font-[Outfit] font-bold mb-6 flex items-center gap-2">
            <Question size={24} weight="bold" className="text-[#3B82F6]" />
            {t('nw.faq_title')} · {content.label}
          </h2>
          <div className="space-y-4" data-testid="niche-faq">
            {content.faqs.map((f, i) => (
              <div key={i} className="p-5 bg-white/[0.02] border border-white/10 rounded-2xl">
                <h3 className="font-[Outfit] font-bold mb-2 text-white">{f.q}</h3>
                <p className="text-sm text-white/70 leading-relaxed">{f.a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal links to other niches */}
      <section className="px-6 pb-16">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-lg font-[Outfit] font-bold mb-4 text-white/80">Explora outros nichos</h2>
          <div className="flex flex-wrap gap-2" data-testid="niche-links">
            {niches.filter((n) => n.slug !== slug).map((n) => (
              <Link key={n.slug} to={`/winners/${n.slug}`}
                className="px-3.5 py-2 bg-white/5 hover:bg-white/10 border border-white/10 hover:border-[#F59E0B]/40 rounded-lg text-sm text-white/70 hover:text-white transition">
                {n.label}{n.count > 0 ? ` (${n.count})` : ''}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="px-6 pb-24">
        <div className="max-w-3xl mx-auto p-10 bg-gradient-to-br from-[#F59E0B]/10 via-[#3B82F6]/5 to-transparent border border-[#F59E0B]/30 rounded-3xl text-center">
          <h2 className="text-3xl font-[Outfit] font-bold mb-3">{t('nw.cta_title', { niche: content.label.toLowerCase() })}</h2>
          <p className="text-white/60 mb-7 max-w-xl mx-auto">{t('nw.cta_sub')}</p>
          <button onClick={handleLogin} data-testid="niche-final-cta"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-[#F59E0B] to-[#FBBF24] rounded-xl text-base font-semibold text-black shadow-2xl shadow-amber-500/20">
            <Rocket size={20} weight="fill" /> {t('nw.cta_btn')} <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      <footer className="px-6 py-8 border-t border-white/10 text-sm text-white/40">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-3">
          <div>© {new Date().getFullYear()} FunnelHack AI</div>
          <div className="flex items-center gap-5">
            <Link to="/" className="hover:text-white transition">{t('hall.footer_home')}</Link>
            <Link to="/hall-of-winners" className="hover:text-white transition">Hall of Winners</Link>
            <Link to="/guia" className="hover:text-white transition">{t('hall.footer_guide')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default NicheWinners;
